import { Accordion, Box, Flex, Text } from "@chakra-ui/react";
import moment from "moment";
import { useContext } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { RanaMarkdown } from "@nens/ranalib";
import { Portal } from "@nens/ranalib";

import { BackButton } from "@/components/back-button";
import { RanaAccordionItem } from "@/components/rana-accordion-item";
import { RanaPageTransition } from "@/components/rana-page-transition";
import { useShareKeyQuery } from "@/queries/query-share-key";
import { usePublicationShareKey } from "@/stores/tenant-provider-public";

import { PublicationNameEditable } from "../../components/publication-name-editable";
import { PublicationVersionFunctionsContext } from "../../components/store-wrapper";
import { getDescription, getIsPublic } from "../../store/selectors";
import { usePublicationStore } from "../../store/store";
import { PublicationActionBar } from "../action-bar";
import { PublicationMap } from "../map";
import { PublicationThumbnail } from "../map-thumbnail/publication-thumbnail";
import { PublicationPublicAccountModalButton } from "../public-view";

import { DraftOrPublishedLabel } from "./components/draft-or-published-label";
import { PublicationDetailFileList } from "./components/file-list";
import { PublicationRouteBlocker } from "./components/route-blocker";
import { usePublicationShowMap } from "./hooks/use-show-map";

export function PublicationDetail() {
  const { closePublication } = useContext(PublicationVersionFunctionsContext);
  const [showMap, setShowMap] = usePublicationShowMap();
  const publicationId = usePublicationStore((state) => state.id);

  const isPublic = usePublicationStore(getIsPublic);
  const description = usePublicationStore(getDescription);
  const intl = useIntl();

  const publicationShareKey = usePublicationShareKey();
  const apiKeyId = publicationShareKey?.split(".")[0];

  const { data: shareKeyData } = useShareKeyQuery(apiKeyId);
  const accessExpiryDate = shareKeyData?.expiresAt;

  return (
    <>
      <PublicationRouteBlocker />
      <Flex
        mb={8}
        flexDirection="column"
        {...(isPublic
          ? {
              flexWrap: "wrap-reverse",
              justifyContent: "space-between",
            }
          : { gap: 5 })}
      >
        {!isPublic && (
          <BackButton back={closePublication}>
            <FormattedMessage id="publication.title" defaultMessage="Publications" />
          </BackButton>
        )}

        <Box>
          <Flex flexWrap="wrap" alignItems="center" justifyContent="space-between">
            <Flex gap={2} alignItems="center">
              {isPublic && (
                <Box mr={4}>
                  <PublicationPublicAccountModalButton />
                </Box>
              )}
              <PublicationThumbnail h={11} publicationId={publicationId} />
              <PublicationNameEditable />
              <DraftOrPublishedLabel />
            </Flex>
            <Box ml="auto">
              <PublicationActionBar />
            </Box>
          </Flex>

          {description && (
            <Accordion allowToggle defaultIndex={0}>
              <RanaAccordionItem
                title={intl.formatMessage({
                  id: "publication.detail.description",
                  defaultMessage: "Description",
                })}
              >
                <Box maxW="prose">
                  <RanaMarkdown readMoreHeight="md" markdown={description} />
                </Box>
              </RanaAccordionItem>
            </Accordion>
          )}
          <Box mt={2}>
            {accessExpiryDate && (
              <FormattedMessage
                id="publication.detail.access_expiry"
                defaultMessage="Access expires in {time}"
                values={{ time: <Text as="strong">{moment(accessExpiryDate).fromNow(true)}</Text> }}
              />
            )}
          </Box>
        </Box>
      </Flex>

      <PublicationDetailFileList />
      <Portal>
        {showMap && (
          <Box position="fixed" zIndex="fullscreenmap">
            <RanaPageTransition visible={showMap}>
              <PublicationMap closeMap={() => setShowMap(false)} />
            </RanaPageTransition>
          </Box>
        )}
      </Portal>
      {/* FUTURE: don't forget to add the link "open publication in normal view" back*/}
      {/* {isPublic && (
        <Flex w="full" justifyContent="flex-end">
          <PublicationPublicGoToNormalViewerButton
            rightIcon={<RightArrowIcon />}
            h="button"
            publication={props.publication}
          />
        </Flex>
      )} */}
    </>
  );
}
